import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, message, Skeleton, Empty, Modal, Form, Input, Select, DatePicker, Row, Col } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { 
  SubtaskListProps, 
  PlanogramSubtask, 
  SubtaskFilters, 
  SubtaskStatus 
} from '../../types/plan-sub-task';
import FilterPanel from '../../components/common/FilterPanel';
import SubtaskTable from './components/SubtaskTable';

import { PlanogramSubtaskService } from '../../services/planogramSubtaskService';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Subtask List Page Component
 * Displays a list of subtasks assigned to the current user with filtering and pagination
 */
const SubtaskList4Worker: React.FC<SubtaskListProps> = ({}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  // State management
  const [subtasks, setSubtasks] = useState<PlanogramSubtask[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filters, setFilters] = useState<SubtaskFilters>({
    // Show all data by default, no user-specific filtering
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [milestones, setMilestones] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);

  // Service instance
  const subtaskService = PlanogramSubtaskService.getInstance();

  // Load reference data
  const loadReferenceData = useCallback(async () => {
    try {
      const [milestonesResponse, assigneesResponse] = await Promise.all([
        subtaskService.getMilestones(),
        subtaskService.getAssignees()
      ]);

      if (milestonesResponse.success) {
        setMilestones(milestonesResponse.data);
      }

      if (assigneesResponse.success) {
        setAssignees(assigneesResponse.data);
      }
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  }, [subtaskService]);

  // Load subtasks data
  const loadSubtasks = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }

    try {
      const response = await subtaskService.getSubtasks4Me(filters, {
        current: pagination.current,
        pageSize: pagination.pageSize
      });

      if (response.success && response.data) {
        setSubtasks(response.data.tableData);
        setPagination(prev => ({
          ...prev,
          total: response.data.paging.totalCount
        }));
      } else {
        message.error(response.errorMessage || t('subtask.loadFailed'));
        setSubtasks([]);
      }
    } catch (error) {
      console.error('Error loading subtasks:', error);
      message.error(t('subtask.loadFailed'));
      setSubtasks([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      setInitialLoading(false);
    }
  }, [subtaskService, filters, pagination.current, pagination.pageSize]);



  // Initial data loading
  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  // 监听filters和pagination变化自动加载数据
  useEffect(() => {
    loadSubtasks();
  }, [filters, pagination.current, pagination.pageSize]);

  // Handle filter changes
  const handleFiltersChange = (values: any) => {
    // 将表单值转换为SubtaskFilters格式
    const newFilters: SubtaskFilters = {
      search: values.search,
      status: values.status,
      milestone: values.milestone,
      assignee: values.assignee,
      dueDateStart: values.dateRange?.[0] ? values.dateRange[0].startOf('day').valueOf() : undefined,
      dueDateEnd: values.dateRange?.[1] ? values.dateRange[1].endOf('day').valueOf() : undefined,
      overdueFlag: values.overdueFlag
    };
    
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 })); // 重置到第一页
    
    // 不需要手动触发搜索，因为useEffect会监听filters变化自动触发
  };


  // Handle filter reset
  const handleReset = () => {
    // 重置筛选条件
    const resetFilters: SubtaskFilters = {
      // 重置为显示所有数据，无默认筛选
    };
    form.resetFields();
    setFilters(resetFilters);
    
    // 重置到第一页
    setPagination(prev => ({ ...prev, current: 1 }));
    
    // 不需要手动触发搜索，因为useEffect会监听filters变化自动触发
  };

  // Handle pagination change
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize
    }));
    // 不需要手动触发搜索，因为useEffect会监听pagination变化自动触发
  };

  // Handle row click (navigate to detail page)
  const handleRowClick = (subtask: PlanogramSubtask) => {
    navigate(`/subtasks/${subtask.subtaskCode}`, { 
      state: { from: '/subtasks4Worker' } 
    });
  };

  // Handle complete task (navigate to completion page)
  const handleCompleteTask = (subtask: PlanogramSubtask) => {
    navigate(`/subtasks/${subtask.subtaskCode}/complete`);
  };

  // Handle start task with confirmation
  const handleStartTask = async (subtask: PlanogramSubtask) => {
    Modal.confirm({
      title: t('subtask.confirmStartTask'),
      width: 500,
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>{t('subtask.confirmStartMessage')}</p>
          <div style={{ 
            background: '#f5f5f5', 
            padding: '12px', 
            borderRadius: '4px', 
            margin: '12px 0',
            border: '1px solid #d9d9d9'
          }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
              {subtask.subtaskCode} - {subtask.subtaskName}
            </p>
            <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
              {t('subtask.relatedPlan')}：{subtask.planName}
            </p>
          </div>
          <div style={{ 
            background: '#e6f7ff', 
            padding: '12px', 
            borderRadius: '4px', 
            border: '1px solid #91d5ff',
            marginTop: '16px'
          }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#1890ff' }}>
              📋 {t('subtask.importantTips')}：
            </p>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
              • {t('subtask.statusSyncTip')}
            </p>
            <p style={{ margin: '0', fontSize: '14px' }}>
              • {t('subtask.preparationTip')}
            </p>
          </div>
        </div>
      ),
      okText: t('subtask.confirmStart'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          setLoading(true);
          const response = await subtaskService.startSubtask(subtask.id, subtask.subtaskCode);
          
          if (response.success) {
            message.success(t('subtask.taskStarted'));
            // Refresh data
            await loadSubtasks(false);
          } else {
            message.error(response.errorMessage || t('subtask.startTaskFailed'));
          }
        } catch (error) {
          console.error('Error starting task:', error);
          message.error(t('subtask.startTaskFailed'));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Handle status update
  const handleStatusUpdate = async (subtaskCode: string, newStatus: SubtaskStatus) => {
    // If it's a start action, use the special start task handler
    if (newStatus === 'Ongoing') {
      const subtask = subtasks.find(s => s.subtaskCode === subtaskCode);
      if (subtask) {
        handleStartTask(subtask);
        return;
      }
    }

    try {
      setLoading(true);
      const response = await subtaskService.updateSubtaskStatus(subtaskCode, newStatus);
      
      if (response.success) {
        message.success(t('subtask.statusUpdateSuccess'));
        // Refresh data
        await loadSubtasks(false);
      } else {
        message.error(response.errorMessage || t('subtask.statusUpdateFailed'));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error(t('subtask.statusUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="subtask-list-container">

      {/* Filter Panel */}
      <FilterPanel
        form={form}
        onFilter={handleFiltersChange}
        onReset={handleReset}
        title={t('menu.mySubtask')}
        collapsible={true}
      >
        {/* 基础筛选条件 */}
        {/* <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item 
            name="search" 
            label={<Text style={{ fontWeight: 500, color: '#262626' }}>{t('subtask.filter.keywordSearch')}</Text>}
            style={{ marginBottom: '12px' }}
          >
            <Input
              placeholder={t('subtask.filter.searchPlaceholder')}
              allowClear
              prefix={<SearchOutlined />}
              disabled={loading}
            />
          </Form.Item>
        </Col> */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item 
            name="status" 
            label={<Text style={{ fontWeight: 500, color: '#262626' }}>{t('subtask.filter.taskStatus')}</Text>}
            style={{ marginBottom: '12px' }}
          >
            <Select
              placeholder={t('subtask.filter.statusPlaceholder')}
              allowClear
              disabled={loading}
            >
              <Option value="all">{t('subtask.filter.all')}</Option>
              <Option value="Pending">{t('subtask.filter.pending')}</Option>
              <Option value="Active">{t('subtask.filter.active')}</Option>
              <Option value="Ongoing">{t('subtask.filter.ongoing')}</Option>
              <Option value="Finished">{t('subtask.filter.finished')}</Option>
              <Option value="Cancelled">{t('subtask.filter.cancelled')}</Option>
              <Option value="Paused">{t('subtask.filter.paused')}</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item 
            name="milestone" 
            label={<Text style={{ fontWeight: 500, color: '#262626' }}>{t('subtask.filter.milestone')}</Text>}
            style={{ marginBottom: '12px' }}
          >
            <Select
              placeholder={t('subtask.filter.milestonePlaceholder')}
              allowClear
              disabled={loading}
            >
              {milestones.map(milestone => (
                <Option key={milestone} value={milestone}>{milestone}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        
        {/* 高级筛选条件 */}
        {/* <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item 
            name="assignee" 
            label={<Text style={{ fontWeight: 500, color: '#262626' }}>{t('subtask.filter.assignee')}</Text>}
            style={{ marginBottom: '12px' }}
          >
            <Input
              placeholder={t('subtask.filter.assigneePlaceholder')}
              allowClear
              disabled={loading}
            />
          </Form.Item>
        </Col> */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item 
            name="dateRange" 
            label={<Text style={{ fontWeight: 500, color: '#262626' }}>{t('subtask.filter.dueDate')}</Text>}
            style={{ marginBottom: '12px' }}
          >
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={[t('subtask.filter.dueDatePlaceholder'), t('subtask.filter.dueDatePlaceholder')]}
              format="YYYY-MM-DD"
              disabled={loading}
            />
          </Form.Item>
        </Col>
        {/* <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item 
            name="overdueFlag" 
            label={<Text style={{ fontWeight: 500, color: '#262626' }}>{t('subtask.filter.overdueStatus')}</Text>}
            style={{ marginBottom: '12px' }}
          >
            <Select
              placeholder={t('subtask.filter.overdueStatusPlaceholder')}
              allowClear
              disabled={loading}
            >
              <Option value="all">{t('subtask.filter.all')}</Option>
              <Option value={true}>{t('subtask.filter.overdue')}</Option>
              <Option value={false}>{t('subtask.filter.notOverdue')}</Option>
            </Select>
          </Form.Item>
        </Col> */}
      </FilterPanel>

      {/* Subtask Table */}
      <Card className="subtask-list-content">
        {subtasks.length === 0 && !loading ? (
          <Empty
            description={t('subtask.noData')}
            style={{ padding: '40px 0' }}
          />
        ) : (
          <SubtaskTable
            subtasks={subtasks}
            loading={loading}
            pagination={pagination}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onStatusUpdate={handleStatusUpdate}
            onCompleteTask={handleCompleteTask}
          />
        )}
      </Card>

    </div>
  );
};

export default SubtaskList4Worker;
